
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'

import { courseBan } from '@xt/client/utils/course/ban'
import InfiniteLoading, { StateChanger } from 'vue-infinite-loading'
import EmptyStatus from '@xt/www/components/emptyStatus/index.vue'
import { EnumEmptyStatus } from '@xt/client/utils/empty-status/index'
import { EnumBuyWay, EnumOrderButtonType, EnumWaterfallLoadResult } from '@xt/client'
import { EnumInvoiceState } from '@xt/client/enums'

import ReceiveGift from './views/ReceiveGift/index.vue'
import OrderItem from './views/OrderItem/index.vue'
import Invoice from './views/Inovice/index.vue'
import OrderDetailPop from './views/OrderDetailPop/index.vue'

import type { UserInfo } from '@xt/client/entities/user/types'
import type { AccountGiftUIData, OrderItem as OrderItemType } from '@xt/client/entities/my-v2/types.d'
import type { RawLocation } from 'vue-router'
import { getGiftsData, transformGiftsForAccountDisplay } from '@xt/client/utils/messageModalQueue/index'

@Observer
@Component({
  components: {
    ReceiveGift,
    OrderItem,
    InfiniteLoading,
    EmptyStatus,
    Invoice,
    OrderDetailPop
  }
})
export default class MyAccountOrder extends Vue {
  @Prop({ required: true }) userInfo: UserInfo

  accountGiftsData: Array<AccountGiftUIData> = []

  @Ref()
  orderInvoicePop: {
    showPop: (item: OrderItemType) => void
  }

  @Ref()
  orderDetailPop: {
    showPop: (item: OrderItemType) => void
  }

  get PageStore() {
    return this.$store.$myV2.Account.Order
  }

  get UserStore() {
    return this.$store.$storeUser
  }

  get EnumEmptyStatus() {
    return EnumEmptyStatus
  }

  async infiniteHandler($state: StateChanger) {
    const result = await this.PageStore.loadOrderList(this.userInfo)

    if (result === EnumWaterfallLoadResult.Cancel) {
      console.log('其他异常')
      return
    }

    if (result === EnumWaterfallLoadResult.Error) {
      $state.error()
    }

    if (result === EnumWaterfallLoadResult.Loaded) {
      $state.loaded()
    }

    if (result === EnumWaterfallLoadResult.Complete) {
      if (this.PageStore.list.length > 0) {
        $state.loaded()
      }
      $state.complete()
    }
  }

  handleOptionsChange(value: string) {
    this.PageStore.onChangeOption(value)

    document.documentElement.scrollTo({ top: 0 })
  }

  handleResetPageData() {
    // 页面滚动到顶部
    document.documentElement.scrollTo({ top: 0 })
    this.PageStore.recalcState(this.userInfo)
  }

  handleTickEnd() {
    this.handleResetPageData()
  }

  handleClickBtn({ item, btnType }: { item: OrderItemType; btnType: EnumOrderButtonType }) {
    // 仅处理真正具有点击事件的按钮
    if (btnType === EnumOrderButtonType.CancelOrder) {
      this.handleCancelOrder(item)
    } else if (btnType === EnumOrderButtonType.ToPay) {
      this.handleToPay(item)
    } else if (btnType === EnumOrderButtonType.InvoiceApply) {
      this.handleApplyInvoice(item)
    } else if (btnType === EnumOrderButtonType.RefundApply) {
      this.handleRefundInvoiceAuth(item)
    } else if (btnType === EnumOrderButtonType.RefundCancel) {
      this.handleCancelRefundInvoiceAuth(item)
    } else if (btnType === EnumOrderButtonType.ReSignup) {
      this.handleResignup(item)
    }
  }

  // 取消订单
  handleCancelOrder(item: OrderItemType) {
    this.$confirmV2({
      desc: '您确认取消订单吗？',
      onOk: async () => {
        await this.$store.$storeOrder.onOrderCancel({
          orderNo: item.orderNo
        })
        await this.$store.$storeUser.onUpdatingUserInfo()
        this.$msg(this.$EnumMessage.cancel_order_success, 'success')
        this.handleResetPageData()
      }
    })
  }

  // 立即支付
  handleToPay(item: OrderItemType) {
    const route: RawLocation = {
      name: 'payment',
      query: {
        orderNo: item.orderNo,
        productId: `${item.raw.productId}`,
        give: `${item.raw.buyWay === EnumBuyWay.Give}`,
        group: `${item.raw.activityType === 1}`,
        memberBeforeOption: 'true'
      }
    }

    this.$router.push(route)
  }

  // 申请发票
  handleApplyInvoice(item: OrderItemType) {
    this.orderInvoicePop.showPop(item)
  }

  async _handleRefund(orderNo: string, showRefundTip: boolean = false) {
    const checkResult = await this.PageStore.onCheckRefund(orderNo)

    if (!checkResult) return

    const refund = async () => {
      try {
        await this.$store.$storeOrder.onOrderRefund(orderNo)
      } catch {}
      await this.$store.$storeUser.onUpdatingUserInfo()
      this.handleResetPageData()
    }

    if (checkResult.adequacy === true) {
      if (showRefundTip) {
        this.$confirmV2({
          desc: '您确认申请退课吗？',
          onOk: async () => {
            await refund()
          }
        })
      } else {
        await refund()
      }
    } else {
      this.$confirmV2({
        desc: `当前订单需扣回${checkResult.consumerCoins}个桐板儿～\n（不足扣回的${checkResult.refundCompensateCoins}个桐板儿是否要用${checkResult.refundCompensateCoins * 8}元抵扣？）`,
        cancelText: '再想想',
        okText: '确认抵扣并退课',
        onOk: async () => {
          await refund()
        }
      })
    }
  }

  // 申请退课
  async handleRefundInvoiceAuth(item: OrderItemType) {
    if (item.invoiceState === EnumInvoiceState.WaitInvoice) {
      this.$confirmV2({
        desc: '申请退课将取消发票申请，是否退课？',
        onOk: async () => {
          await this._handleRefund(item.orderNo)
        }
      })
    } else if (item.invoiceState === EnumInvoiceState.HasInvoice) {
      this.$confirmV2({
        desc: '退款将作废已开具发票，是否退课？',
        onOk: async () => {
          await this._handleRefund(item.orderNo)
        }
      })
    } else {
      await this._handleRefund(item.orderNo, true)
    }
  }

  // 取消退课
  handleCancelRefundInvoiceAuth(item: OrderItemType) {
    this.$confirmV2({
      desc: '您确认取消退课吗？',
      onOk: async () => {
        await this.$store.$storeOrder.onOrderCancelRefund({
          orderNo: item.raw.orderNo,
          refundOrderSn: item.raw.refundOrderSn
        })
        await this.$store.$storeUser.onUpdatingUserInfo()
        this.handleResetPageData()
      }
    })
  }

  // 再次购买
  async handleResignup(item: OrderItemType) {
    // 检测课程是否在ban列表中，如果在ban列表中则直接toast "课程已结束"
    const isBan = await courseBan.isBan(item.raw.productId)
    if (isBan) {
      this.$msg('课程已结束')
      return
    }

    if (item.raw.activityPageUrl) {
      this.$router.push({ path: item.raw.activityPageUrl })
    } else {
      this.$router.push({
        path: `/commodity/${item.raw.productId}`
      })
    }
  }

  hanldeInvoiceSuccess() {
    this.handleResetPageData()
  }

  // 展示订单详情
  handleShowOrderDetail({ item }: { item: OrderItemType }) {
    this.orderDetailPop.showPop(item)
  }

  async initPage() {
    const res = await getGiftsData()

    if (!res.length) return

    this.accountGiftsData = transformGiftsForAccountDisplay(res)
  }

  created(): void {
    this.initPage()
  }

  mounted(): void {}

  updated(): void {}

  destroyed(): void {}
}
